import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { PageLayout } from "@/components/layout/PageLayout";
import { AuthProvider } from "@/lib/auth";
import Home from "@/pages/Home";

const Calculators = lazy(() => import("@/pages/Calculators"));
const CalculatorDetail = lazy(() => import("@/pages/CalculatorDetail"));
const Articles = lazy(() => import("@/pages/Articles"));
const ArticleDetail = lazy(() => import("@/pages/ArticleDetail"));
const AIPage = lazy(() => import("@/pages/AIPage"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Account = lazy(() => import("@/pages/Account"));
const SignIn = lazy(() => import("@/pages/SignIn"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const BillingCancel = lazy(() => import("@/pages/BillingCancel"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AdminAffiliates = lazy(() => import("@/pages/AdminAffiliates"));
const BuildingCreditGuide = lazy(() => import("@/pages/BuildingCreditGuide"));
const Privacy = lazy(() => import("@/pages/Legal").then((module) => ({ default: module.Privacy })));
const Terms = lazy(() => import("@/pages/Legal").then((module) => ({ default: module.Terms })));
const Disclaimer = lazy(() => import("@/pages/Legal").then((module) => ({ default: module.Disclaimer })));
const EditorialPolicy = lazy(() => import("@/pages/TrustPages").then((module) => ({ default: module.EditorialPolicy })));
const AdvertisingDisclosure = lazy(() => import("@/pages/TrustPages").then((module) => ({ default: module.AdvertisingDisclosure })));
const HowWeMakeMoney = lazy(() => import("@/pages/TrustPages").then((module) => ({ default: module.HowWeMakeMoney })));
const BorrowPage = lazy(() => import("@/pages/CategoryPages").then((module) => ({ default: module.BorrowPage })));
const InvestPage = lazy(() => import("@/pages/CategoryPages").then((module) => ({ default: module.InvestPage })));
const BusinessPage = lazy(() => import("@/pages/CategoryPages").then((module) => ({ default: module.BusinessPage })));
const RealEstatePage = lazy(() => import("@/pages/CategoryPages").then((module) => ({ default: module.RealEstatePage })));
const VehiclesPage = lazy(() => import("@/pages/CategoryPages").then((module) => ({ default: module.VehiclesPage })));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo({ top: 0, behavior: "instant" }), [pathname]);
  return null;
}

function RouteLoading() {
  return <div className="mml-container min-h-[calc(100vh-4.5rem)] py-16 text-center text-slate-600" role="status">Loading MyMoneyLocal…</div>;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <PageLayout>
            <Suspense fallback={<RouteLoading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/calculators" element={<Calculators />} />
                <Route path="/calculators/:slug" element={<CalculatorDetail />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/articles/:slug" element={<ArticleDetail />} />
                <Route path="/borrow" element={<BorrowPage />} />
                <Route path="/invest" element={<InvestPage />} />
                <Route path="/business" element={<BusinessPage />} />
                <Route path="/real-estate" element={<RealEstatePage />} />
                <Route path="/vehicles" element={<VehiclesPage />} />
                <Route path="/ai" element={<AIPage />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/account" element={<Account />} />
                <Route path="/sign-in" element={<SignIn />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/billing/cancel" element={<BillingCancel />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/editorial-policy" element={<EditorialPolicy />} />
                <Route path="/advertising-disclosure" element={<AdvertisingDisclosure />} />
                <Route path="/how-we-make-money" element={<HowWeMakeMoney />} />
                <Route path="/admin/affiliates" element={<AdminAffiliates />} />
                <Route path="/learn/building-credit" element={<BuildingCreditGuide />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </PageLayout>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
